import { MediaDto } from './products.dto';

/**
 * Raw API shapes for the Subscription Shop endpoints (`v1.0/subscription/*`).
 * Mirrors the swagger schemas SubscriptionProductListModel, SubscriptionBillingPeriodListModel,
 * CartLineSubscriberEditModel and CartLineSubscriberValidationResult.
 */
/** swagger: SubscriptionBillingPeriodType (integer enum). */
export enum SubscriptionBillingPeriodTypeDto {
  Yearly = 0,
  Halfyearly = 1,
  Quarterly = 2,
  Monthly = 3,
}

/** swagger: SubscriptionType (integer enum). */
export enum SubscriptionTypeDto {
  Passepartout = 0, //non-recurring
  Punchcard = 1, //non-recurring
  RecurringSubscription = 2, //recurring
}

export interface SubscriptionBillingPeriodListDto {
  id: string;
  period_type: SubscriptionBillingPeriodTypeDto;
  price: number | null;
  original_price: number | null;
  tax_rate: number | null;
  /** The period price spread over its months (e.g. quarterly ÷ 3) — the "only €X per month" line. */
  price_per_month: number | null;
  /** Whole-percent saving vs. the worst per-month rate on offer; null on the baseline period or when there is no saving. */
  savings_percentage: number | null;

  first_allowed_start_date: string | null;
}

/**
 * swagger: SubscriptionStartDateRequirement (integer enum). Whether the buyer chooses the
 * subscription's start date while buying. Deliberately its own enum, not the `UserDetailRequirement`
 * the API uses for the subscriber's personal details — those the shop no longer collects at all (the
 * holder fills them in the web portal), and there is no `RequiredDontDisplay` counterpart here
 * anyway, because a date nobody is shown is a date nobody can pick.
 */
export enum SubscriptionStartDateRequirementDto {
  /** No picker; the subscription starts on its first allowed day. */
  NotRequired = 0,
  /** Picker shown, with the first allowed day as its minimum; left empty it starts on that day. */
  Optional = 1,
  /** The order is refused (`subscription_start_date_required`) until a date is chosen. */
  Required = 2,
}

export interface SubscriptionProductListDto {
  id: string;
  public_name: string | null;
  /** Tagline shown under the product name, set in the dashboard under product → General. */
  subtitle: string | null;
  description: string | null;
  subscription_type: SubscriptionTypeDto;
  requires_account: boolean;
  /** Reaches the shop on the same channels as `requires_account`; absent on an older backend. */
  start_date_requirement: SubscriptionStartDateRequirementDto | null;
  /** Unused by the shop: it never personalises a line, the web portal invites the holders instead. */
  is_personal: boolean;
  photo_id: string | null;
  photo: MediaDto | null;
  price: number | null;
  original_price: number | null;
  tax_rate: number | null;
  /** Ordered shortest-period-first by the backend — render as returned, don't re-sort. */
  billing_periods: SubscriptionBillingPeriodListDto[] | null;
  sepa_direct_debit_enabled: boolean;
  /** The company's mandate terms & conditions page — must be linked at checkout when SEPA is enabled. */
  sepa_terms_and_conditions_url: string | null;
  /** Company-authored HTML explaining the mandate; null until an admin fills it in, which is the norm. */
  sepa_description_html: string | null;
  /** Lifetime sell cap reached — add attempts return `subscription_sold_out`. */
  is_sold_out: boolean;
}

/** swagger: CartLineSubscriberEditModel — request body for PUT subscription/cart/edit/{cartLineId}. */
export interface CartLineSubscriberEditDto {
  billing_period_id?: string | null;
  start_date?: string | null; // ISO date-time
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  birthday?: string | null; // ISO date-time
  gender?: string | null;
  photo_id?: string | null;
  address?: string | null;
  zipcode?: string | null;
  city?: string | null;
  country?: string | null;
}

export interface CartLineSubscriberValidationErrorDto {
  field: string | null;
  kind: string | null;
  /** Positional values for the kinds whose message names one (e.g. the first allowed start date). */
  params: string[] | null;
}

export interface CartLineSubscriberValidationResultDto {
  cart_line_id: string;
  product_id: string | null;
  is_valid: boolean;
  errors: CartLineSubscriberValidationErrorDto[] | null;
  warnings: CartLineSubscriberValidationErrorDto[] | null;
}

/** swagger: SubscriptionCardStatus (integer enum). */
export enum SubscriptionCardStatusDto {
  Active = 0,
  Blocked = 1,
}

/** swagger: SubscriptionInstanceStatus (integer enum). */
export enum SubscriptionInstanceStatusDto {
  Active = 0,
  Paused = 1,
  Blocked = 2,
  Cancelled = 3,
  Expired = 4,
  PendingCard = 5,
  PendingMandate = 6,
}

/** swagger: SubscriptionCardValidateModel — request body for POST subscription-instances/cards/validate. */
export interface SubscriptionCardValidateDto {
  card_id: string;
  product_id: string;
  movie_show_id: string;
  company_id: string;
}

/**
 * swagger: SubscriptionCardValidationResult. A failed check comes back as 200 with `valid: false`
 * (not a 400): `error` holds the rule kind, `error_message` an optional human-readable text.
 */
export interface SubscriptionCardValidationResultDto {
  valid: boolean;
  error: string | null;
  error_message: string | null;
  error_args: string[] | null;
  subscription_card_id: string | null;
  subscription_instance_id: string | null;
  subscription_id: string | null;
  subscription_name: string | null;
}

/** swagger: SubscriptionPortalListModel — only the fields the shop reads. */
export interface SubscriptionPortalListDto {
  id: string;
  product_name: string | null;
  subscription_type: SubscriptionTypeDto;
  status: SubscriptionInstanceStatusDto;
  /**
   * The instance's own personalisation, not the account name — every pass in a household carries its
   * own holder, which is what tells two cards of one account apart in the dropdown (PAUL-3209). Null
   * until the pass has been personalised.
   */
  holder_first_name?: string | null;
}

/** swagger: SubscriptionPortalCardModel. */
export interface SubscriptionPortalCardDto {
  id: string;
  card_id: string | null;
  status: SubscriptionCardStatusDto;
  is_digital: boolean;
  blocked_at: string | null;
}

/**
 * swagger: CartSubscriptionCardOptionModel — a card offered on one cart line (PAUL-3557).
 *
 * `card_id` is the card *code*, and the only one of the three ids the attach call accepts as
 * `subscription_card_code`. `subscription_card_id` is the row id and is rejected there.
 */
export interface CartSubscriptionCardOptionDto {
  card_id: string | null;
  subscription_card_id: string;
  subscription_instance_id: string;
  product_name: string | null;
  holder_first_name: string | null;
  holder_last_name: string | null;
  is_digital: boolean;
  /** PAUL-3698 — `true` in `cards`, `false` in `unavailable_cards`. Optional for a backend that predates it. */
  available?: boolean;
  /** The error kind the attach call would answer this card with on this line. `null` when available. */
  unavailable_reason?: string | null;
  /** The backend's own translation of that kind, any date already filled in. `null` when available. */
  unavailable_message?: string | null;
}

/**
 * swagger: CartSubscriptionCardLineModel — one subscription-ticket line with the cards the signed-in
 * account may redeem *on that line* (PAUL-3557). Per line and not per account because redeemability
 * depends on the show: the subscription's validity window is compared against the show date, and the
 * per-show and max-ticket caps count the cart's other lines. A card absent from `cards` cannot be used.
 *
 * `unavailable_cards` (PAUL-3698) holds the account's cards refused on this line, each with the reason,
 * so they can be shown disabled. A card refused on every line (blocked, or its subscription has ended)
 * is in neither list.
 */
export interface CartSubscriptionCardLineDto {
  cart_line_id: string;
  movie_show_id: string | null;
  product_id: string | null;
  cards: CartSubscriptionCardOptionDto[] | null;
  unavailable_cards?: CartSubscriptionCardOptionDto[] | null;
}
